const SESSION_GEOCODE_KEY = 'vitaldata.geocodeCache.v1';

export function loadGeocodeCache() {
  try {
    const raw = sessionStorage.getItem(SESSION_GEOCODE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveGeocodeCache(cache) {
  try { sessionStorage.setItem(SESSION_GEOCODE_KEY, JSON.stringify(cache)); }
  catch { /* quota exceeded — continue without cache */ }
}

/**
 * Build a single-line postal address from facility fields.
 * Returns '' when the address is too weak to geocode usefully
 * (e.g. state-only "TX" — Google parks that in central Texas).
 * Require at least a street or a city.
 */
export function buildAddressString(f) {
  const street = f?.street && String(f.street).trim();
  const city = f?.city && String(f.city).trim();
  if (!street && !city) return '';
  const parts = [street, city, f.state, f.postalCode, f.country]
    .filter(p => p && String(p).trim().length > 0)
    .map(p => String(p).trim());
  return parts.join(', ');
}

/**
 * Geocode facilities missing lat/lng via the Maps JS Geocoder.
 * Returns a map of veId -> { lat, lng }.
 */
export async function geocodeFacilities(facilities, { onProgress } = {}) {
  if (!window.google?.maps?.Geocoder) return {};
  const geocoder = new window.google.maps.Geocoder();
  const cache = loadGeocodeCache();
  const out = {};
  let pending = facilities.length;

  for (const f of facilities) {
    pending -= 1;
    if (onProgress) onProgress(facilities.length - pending, facilities.length);

    const addr = f._address || buildAddressString(f);
    if (!addr) continue;

    const cached = cache[addr];
    if (cached) {
      out[f.veId] = cached;
      continue;
    }

    try {
      const { results } = await geocoder.geocode({ address: addr });
      if (results?.[0]) {
        const { lat, lng } = results[0].geometry.location.toJSON();
        const entry = { lat, lng };
        cache[addr] = entry;
        saveGeocodeCache(cache);
        out[f.veId] = entry;
      }
    } catch {
      /* skip bad addresses / quota errors */
    }
    await new Promise(r => setTimeout(r, 60));
  }

  return out;
}
